import { useEffect, useState } from "react";

import { CloseConfirmation } from "@/design-system/components/CloseConfirmation/CloseConfirmation";
import {
  MultistepControlRef,
  MultistepProvider,
  MultistepStepConfig,
  useMultistepContext,
} from "./context";
import { Drawer } from "../Drawer/Drawer";

interface BottomDrawerMultistepModalProps<Data, ResultData> {
  steps: MultistepStepConfig[];
  initialData: Data;
  title: string;
  open: boolean;
  onChangeData?: (data: Data) => void;
  onClose: () => void;
  onComplete?: (resultData: ResultData) => void;
  controlRef?: MultistepControlRef;
  skipCloseConfirmation?: boolean;
}

export function BottomDrawerMultistepModal<Data, ResultData>(
  props: BottomDrawerMultistepModalProps<Data, ResultData>,
) {
  const {
    steps,
    initialData,
    onChangeData,
    onComplete = () => {},
    controlRef,
  } = props;

  return (
    <MultistepProvider
      steps={steps}
      initialData={initialData}
      onChangeData={onChangeData}
      onComplete={onComplete}
      controlRef={controlRef}
    >
      <BottomDrawerMultistepModalContent {...props} />
    </MultistepProvider>
  );
}

function BottomDrawerMultistepModalContent<Data, ResultData>({
  title,
  onClose,
  open,
  skipCloseConfirmation = false,
}: BottomDrawerMultistepModalProps<Data, ResultData>) {
  const [closeConfirmationOpen, setCloseConfirmationOpen] = useState(false);
  const { currentStepIndex, steps, reset } = useMultistepContext<
    Data,
    ResultData
  >();
  const currentStep = steps[currentStepIndex];

  function handleCloseConfirm() {
    reset();
    setCloseConfirmationOpen(false);
    onClose();
  }

  function handleCloseAttempt() {
    if (skipCloseConfirmation) {
      handleCloseConfirm();
    } else {
      setCloseConfirmationOpen(true);
    }
  }

  // This prompts the user before the page is refreshed or navigated away from
  // TODO (joey): client-side navigation including browser back/forward aren't
  // handled by this. I tried handling this using react-router's <Prompt> and
  // history.block, but they were both buggy. I didn't try useBlocker, because
  // it requires us to upgrade react-router to v6.
  useEffect(() => {
    if (open) {
      window.onbeforeunload = () => true;
      return function () {
        window.onbeforeunload = null;
      };
    }
  }, [open]);

  return (
    <>
      <Drawer
        title={title}
        open={open}
        onOpenChange={handleCloseAttempt}
        placement="bottom"
      >
        <currentStep.Component />
      </Drawer>
      <CloseConfirmation
        open={closeConfirmationOpen}
        onConfirm={handleCloseConfirm}
        onCancel={() => setCloseConfirmationOpen(false)}
      />
    </>
  );
}
